import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { AdminPanelServicePorts } from "../../model";

import { URLS } from "./consts";
import {
  GetCreationDateOfFirstOrder,
  GetOrderStatistics,
  GetRevenueForYear,
  SendEmail,
} from "./ios";

import { Response } from "~/shared/api";
import { AuthenticationError, UnexpectedError } from "~/shared/error";

class ApiProvider extends AdminPanelServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override getOrderStatistics(): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    AdminPanelServicePorts.ProviderIos.GetOrderStatistics.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getOrderStatistics), GetOrderStatistics.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) {
          const {
            totalOrders,
            newOrders,
            completedOrders,
            totalRevenue,
            totalOrdersPercentageChange,
            newOrdersPercentageChange,
            completedOrdersPercentageChange,
            totalRevenuePercentageChange,
          } = response.body.data;
          return taskEither.right({
            ordersToday: {
              value: totalOrders,
              relativeToYesterday: totalOrdersPercentageChange,
            },
            newOrders: {
              value: newOrders,
              relativeToYesterday: newOrdersPercentageChange,
            },
            completedOrders: {
              value: completedOrders,
              relativeToYesterday: completedOrdersPercentageChange,
            },
            revenue: {
              value: totalRevenue,
              relativeToYesterday: totalRevenuePercentageChange,
            },
          } satisfies AdminPanelServicePorts.ProviderIos.GetOrderStatistics.Out);
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override sendEmail({
    to,
    ...params
  }: AdminPanelServicePorts.ProviderIos.SendEmail.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    void
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.sendEmail, {
            ...params,
            toEmails: to,
          }),
        SendEmail.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getRevenueForYear(
    params: AdminPanelServicePorts.ProviderIos.GetRevenueForYear.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    AdminPanelServicePorts.ProviderIos.GetRevenueForYear.Out
  > {
    return function_.pipe(
      Response.process(
        () => this.axios.post(URLS.getRevenueForYear, params),
        GetRevenueForYear.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) {
          const { values } = response.body.data;
          return taskEither.right(values);
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getCreationDateOfFirstOrder(): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    AdminPanelServicePorts.ProviderIos.GetCreationDateOfFirstOrder.Out
  > {
    return function_.pipe(
      Response.process(
        () => this.axios.get(URLS.getCreationDateOfFirstOrder),
        GetCreationDateOfFirstOrder.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) {
          const { firstOrderDate } = response.body.data;
          return taskEither.right(
            typeof firstOrderDate === "string" ? new Date(firstOrderDate) : null,
          );
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }
}

export { ApiProvider };
